import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Table, Tag, Empty, Modal, List, Typography } from "antd";
import dayjs from "dayjs";
import { UiSectionCard, UiButton } from "../../ui";
import useQuery from "../../../hooks/useQuery";
import { detectAdmissionType } from "../ClinicalPractitioner/admissionContext";
import { getPharmacyReturnsListApi } from "../../../services/pharmacyReturns.services";
import { getPharmacyBillsForAdmissionApi } from "../../../services/pharmacyBilling.services";
import RaiseReturnModal from "../../Pharmacy/Returns/RaiseReturnModal";

const STATUS_COLOR = {
  requested: "orange",
  partially_approved: "gold",
  approved: "green",
  rejected: "red",
};

const STATUS_LABEL = {
  requested: "Requested",
  partially_approved: "Partially Approved",
  approved: "Approved",
  rejected: "Rejected",
};

const NursePharmacyReturns = () => {
  const location = useLocation();
  const query = useQuery();
  const admissionType = detectAdmissionType(location.pathname);
  const admissionId = query?.patient;

  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [billPickerOpen, setBillPickerOpen] = useState(false);
  const [raiseBill, setRaiseBill] = useState(null);

  const admissionParams = () =>
    new URLSearchParams(
      admissionType === "OP" ? { op_consultation: admissionId } : { ip_admission: admissionId }
    );

  useEffect(() => {
    if (!admissionId) return;
    setLoading(true);
    getPharmacyReturnsListApi(admissionParams())
      .then((res) => setReturns(res?.data?.results || []))
      .catch(() => setReturns([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionId, admissionType, refresh]);

  useEffect(() => {
    if (!admissionId) return;
    getPharmacyBillsForAdmissionApi(admissionParams())
      .then((res) => setBills(res?.data || []))
      .catch(() => setBills([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionId, admissionType]);

  const handleRaiseReturnClick = () => {
    if (!bills.length) return;
    if (bills.length === 1) {
      setRaiseBill(bills[0]);
      return;
    }
    setBillPickerOpen(true);
  };

  const pickBill = (bill) => {
    setBillPickerOpen(false);
    setRaiseBill(bill);
  };

  const columns = [
    { title: "Return Number", dataIndex: "return_number", key: "return_number" },
    { title: "Bill Number", dataIndex: "bill_number", key: "bill_number" },
    {
      title: "Items",
      key: "items",
      render: (_, record) => record.items?.length || 0,
    },
    { title: "Amount", dataIndex: "total_amount", key: "total_amount", render: (v) => v?.toFixed(2) },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (v) => (v ? dayjs(v).format("DD MMM, YYYY hh:mm A") : "-"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => <Tag color={STATUS_COLOR[value] || "default"}>{STATUS_LABEL[value] || value}</Tag>,
    },
  ];

  return (
    <UiSectionCard
      title="Returns"
      extra={
        <UiButton
          size="sm"
          disabled={!bills.length}
          onClick={handleRaiseReturnClick}
          title={!bills.length ? "No bills to return against yet" : undefined}
        >
          Raise Return
        </UiButton>
      }
    >
      <Table
        loading={loading}
        dataSource={returns}
        columns={columns}
        rowKey="id"
        pagination={false}
        locale={{ emptyText: <Empty description="No returns raised for this patient yet" /> }}
      />

      <Modal
        title="Select a Bill"
        open={billPickerOpen}
        onCancel={() => setBillPickerOpen(false)}
        footer={null}
        centered
      >
        <List
          dataSource={bills}
          renderItem={(bill) => (
            <List.Item onClick={() => pickBill(bill)} style={{ cursor: "pointer" }}>
              <List.Item.Meta
                title={bill.bill_number}
                description={
                  <Typography.Text type="secondary">
                    {dayjs(bill.created_at).format("DD MMM, YYYY")} - {bill.grand_total?.toFixed(2)}
                  </Typography.Text>
                }
              />
            </List.Item>
          )}
        />
      </Modal>

      {raiseBill && (
        <RaiseReturnModal
          open={Boolean(raiseBill)}
          setOpen={(isOpen) => {
            if (!isOpen) setRaiseBill(null);
          }}
          bill={raiseBill}
          refreshPage={() => setRefresh((prev) => !prev)}
        />
      )}
    </UiSectionCard>
  );
};

export default NursePharmacyReturns;
